<template>
  <!--
    Client restore workspace (Backup restore module). Shows everything the old
    server had for ONE backup client next to its match status in the current
    system, and lets the user tick exactly what to bring back. Nothing is sent
    from here: "Restore selected" hands a plan to the view's confirmation modal.
  -->
  <b-modal v-model="show" size="xl" centered no-close-on-backdrop hide-footer
           dialog-class="bk-ws-dialog" content-class="bk-ws-content" body-class="bk-ws-body"
           header-class="bk-ws-header" @hidden="onHidden">
    <template #modal-title>
      <i class="fa-solid fa-boxes-packing mr-50 text-primary"></i>{{ $t('backup.workspace.title') }}
      <span v-if="client" class="bk-id ml-50">{{ $t('backup.backupShort') }} #{{ client.backupId }}</span>
    </template>

    <div v-if="loading && !detail" class="bk-ws-center">
      <b-spinner variant="primary"/>
      <div class="mt-1 text-muted">{{ $t('backup.workspace.loading') }}</div>
    </div>

    <div v-else-if="error" class="bk-ws-center">
      <i class="fa-solid fa-plug-circle-exclamation bk-ws-center__icon"></i>
      <div class="bk-ws-center__title">{{ $t('backup.loadError') }}</div>
      <div class="text-muted">{{ error }}</div>
      <b-button size="sm" variant="outline-primary" class="mt-1" @click="load">
        <i class="fa-solid fa-rotate-right mr-25"></i>{{ $t('backup.retry') }}
      </b-button>
    </div>

    <template v-else-if="detail && client">
      <!-- ============ HERO ============ -->
      <div class="bk-ws-hero">
        <span class="bk-avatar bk-avatar--lg" :class="`bk-avatar--${(client.matchStatus || '').toLowerCase()}`">{{ bkInitials(client) }}</span>
        <div class="bk-ws-hero__main">
          <div class="bk-ws-hero__name">
            {{ bkClientName(client) }}
            <backup-status-badge :status="client.matchStatus" :reason="client.matchReason"
                                 :current-id="client.currentId" :client-id="client.currentId"/>
          </div>
          <div class="bk-ws-hero__meta">
            <span v-if="client.email"><i class="fa-regular fa-envelope"></i>{{ client.email }}</span>
            <span v-if="client.phone"><i class="fa-solid fa-phone"></i>{{ client.phone }}</span>
            <span v-if="client.country"><i class="fa-regular fa-flag"></i>{{ client.country }}</span>
            <span v-if="client.orgId"><i class="fa-solid fa-landmark"></i>{{ getOrganizationNameById(client.orgId) }}<template v-if="client.businessUnitId"> · {{ getBusinessUnitNameById(client.businessUnitId) }}</template></span>
            <span v-if="client.statusId" :style="{ color: bkClientStatusColor(client.statusId) }"><i class="fa-solid fa-stream"></i>{{ bkClientStatusName(client.statusId) }}</span>
            <span v-if="client.representativeUserId"><i class="fa-solid fa-user-tie"></i>{{ bkUserName(client.representativeUserId) }}</span>
            <span v-if="client.creationTime"><i class="fa-regular fa-clock"></i>{{ bkDateTime(client.creationTime) }}</span>
          </div>
        </div>
        <div class="bk-ws-hero__actions">
          <b-button v-if="client.currentId" size="sm" variant="outline-primary" @click="showClient(client.currentId)">
            <i class="fa-solid fa-arrow-up-right-from-square mr-50"></i>{{ $t('backup.openCurrentClient') }} #{{ client.currentId }}
          </b-button>
          <b-button size="sm" variant="link" class="bk-ghost btn-icon" :disabled="loading" v-b-tooltip.hover :title="$t('backup.refresh')" @click="load">
            <i class="fa-solid fa-rotate-right" :class="{ 'fa-spin': loading }"></i>
          </b-button>
        </div>
      </div>

      <div class="bk-ws-callout" :class="`bk-ws-callout--${(client.matchStatus || '').toLowerCase()}`">
        <i :class="calloutIcon"></i>
        <span>{{ calloutText }}</span>
      </div>

      <!-- ============ TOOLBAR ============ -->
      <div class="bk-ws-toolbar">
        <b-button size="sm" variant="outline-primary" :disabled="!anythingRestorable" @click="selectAllMissing">
          <i class="fa-solid fa-wand-magic-sparkles mr-50"></i>{{ $t('backup.workspace.selectAllMissing') }}
        </b-button>
        <b-button size="sm" variant="link" class="bk-ghost" :disabled="!selectedTotal" @click="clearSelection">
          {{ $t('backup.workspace.clearSelection') }}
        </b-button>
        <b-form-checkbox v-model="hideExisting" switch class="ml-auto bk-ws-toolbar__switch">
          {{ $t('backup.workspace.hideExisting') }}
        </b-form-checkbox>
      </div>

      <!-- ============ SECTIONS ============ -->
      <div class="bk-ws-scroll">
        <!-- Client -->
        <section v-if="clientRestorable" class="bk-ws-sec" :class="{ 'is-active': sel.client }">
          <div class="bk-ws-sec__head">
            <b-form-checkbox :checked="sel.client" @change="setClient"/>
            <i class="fa-solid fa-user bk-ws-sec__icon"></i>
            <div class="bk-ws-sec__title">{{ $t('backup.workspace.restoreClient') }}</div>
            <div class="bk-ws-sec__hint">
              {{ client.matchStatus === 'ID_CONFLICT' ? $t('backup.workspace.clientNewId', { id: client.backupId }) : $t('backup.workspace.clientSameId', { id: client.backupId }) }}
            </div>
          </div>
        </section>

        <!-- Field differences -->
        <section v-if="diffs.length" class="bk-ws-sec">
          <div class="bk-ws-sec__head" @click="toggleCollapse('fields')">
            <span class="bk-ws-sec__chk" @click.stop><b-form-checkbox :checked="allOn('fields', diffs.map(d => d.field))"
                             :indeterminate="someOn('fields', diffs.map(d => d.field))"
                             @change="v => setMany('fields', diffs.map(d => d.field), v)"/></span>
            <i class="fa-solid fa-code-compare bk-ws-sec__icon"></i>
            <div class="bk-ws-sec__title">{{ $t('backup.workspace.fieldDiffs') }}</div>
            <span class="bk-ws-count">{{ diffs.length }}</span>
            <div class="bk-ws-sec__hint">{{ $t('backup.workspace.fieldDiffsHint') }}</div>
            <i class="fa-solid bk-ws-sec__chev" :class="collapsed.fields ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
          </div>
          <div v-show="!collapsed.fields" class="bk-ws-sec__body">
            <div class="bk-diff bk-diff--head">
              <span></span><span>{{ $t('backup.workspace.field') }}</span>
              <span>{{ $t('backup.workspace.backupValue') }}</span><span></span>
              <span>{{ $t('backup.workspace.currentValue') }}</span>
            </div>
            <div v-for="d in diffs" :key="d.field" class="bk-diff" :class="{ 'is-on': sel.fields[d.field] }" @click="setOne('fields', d.field, !sel.fields[d.field])">
              <span @click.stop><b-form-checkbox :checked="!!sel.fields[d.field]" @change="v => setOne('fields', d.field, v)"/></span>
              <span class="bk-diff__field">{{ bkFieldLabel(d.field) }}</span>
              <span class="bk-diff__val bk-diff__val--backup" :style="d.field === 'statusId' ? { color: bkClientStatusColor(d.backupValue) } : null">{{ bkFieldValue(d.field, d.backupValue) || '∅' }}</span>
              <i class="fa-solid fa-arrow-right-arrow-left bk-diff__arrow"></i>
              <span class="bk-diff__val" :style="d.field === 'statusId' ? { color: bkClientStatusColor(d.currentValue) } : null">{{ bkFieldValue(d.field, d.currentValue) || '∅' }}</span>
            </div>
          </div>
        </section>

        <!-- Trading accounts -->
        <section v-if="accounts.length" class="bk-ws-sec">
          <div class="bk-ws-sec__head" @click="toggleCollapse('accounts')">
            <span class="bk-ws-sec__chk" @click.stop><b-form-checkbox :checked="allOn('accounts', ids(selectableAccounts))"
                             :indeterminate="someOn('accounts', ids(selectableAccounts))"
                             :disabled="!selectableAccounts.length"
                             @change="v => setAccounts(selectableAccounts, v)"/></span>
            <i class="fa-solid fa-id-card bk-ws-sec__icon"></i>
            <div class="bk-ws-sec__title">{{ $t('backup.workspace.accounts') }}</div>
            <section-counts :restorable="selectableAccounts.length" :total="accounts.length"/>
            <div class="bk-ws-sec__hint">{{ $t('backup.workspace.accountsHint') }}</div>
            <i class="fa-solid bk-ws-sec__chev" :class="collapsed.accounts ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
          </div>
          <div v-show="!collapsed.accounts" class="bk-ws-sec__body">
            <table class="bk-ws-table">
              <thead><tr>
                <th></th><th>{{ $t('backup.columns.account') }}</th><th>{{ $t('backup.columns.group') }}</th>
                <th>{{ $t('backup.columns.leverage') }}</th><th class="text-right">{{ $t('backup.columns.backupBalance') }}</th>
                <th>{{ $t('backup.columns.login') }}</th><th>{{ $t('backup.columns.match') }}</th>
              </tr></thead>
              <tbody>
              <tr v-for="a in visible(accounts)" :key="a.backupId" :class="rowCls(isAccountSelectable(a), sel.accounts[a.backupId])"
                  @click="isAccountSelectable(a) && setAccounts([a], !sel.accounts[a.backupId])">
                <td class="bk-ws-table__chk" @click.stop>
                  <b-form-checkbox :checked="!!sel.accounts[a.backupId]" :disabled="!isAccountSelectable(a)"
                                   @change="v => setAccounts([a], v)"/>
                </td>
                <td><span class="font-weight-bold">{{ a.backupId }}</span>
                  <span class="bk-tag" :class="a.isDemo ? '' : 'bk-tag--real'">{{ a.isDemo ? 'DEMO' : 'REAL' }}</span>
                  <span class="bk-tag">{{ a.currency }}</span></td>
                <td>{{ a.group || '—' }}</td>
                <td>{{ a.leverage ? `1:${a.leverage}` : '—' }}</td>
                <td class="text-right">
                  <div class="bk-two text-right">
                    <span>{{ bkMoney(a.balance, a.currency) }}</span>
                    <small v-if="a.credit">{{ $t('backup.columns.credit') }} {{ bkMoney(a.credit, a.currency) }}</small>
                  </div>
                </td>
                <td>
                  <span v-if="isAccountSelectable(a)" class="bk-tag" :class="a.willReuseId ? 'bk-tag--ok' : 'bk-tag--warn'"
                        v-b-tooltip.hover.top="a.willReuseId ? $t('backup.workspace.sameLoginHint') : $t('backup.workspace.newLoginHint')">
                    {{ a.willReuseId ? $t('backup.workspace.sameLogin') : $t('backup.workspace.newLogin') }}
                  </span>
                </td>
                <td><backup-status-badge :status="a.matchStatus" :reason="a.matchReason" :current-id="a.currentId"/></td>
              </tr>
              </tbody>
            </table>
            <div v-if="selectedIds('accounts').length" class="bk-ws-note">
              <i class="fa-solid fa-circle-info"></i> {{ $t('backup.workspace.accountsZeroHint') }}
            </div>
          </div>
        </section>

        <!-- Monetary -->
        <section v-if="monetary.length" class="bk-ws-sec">
          <div class="bk-ws-sec__head" @click="toggleCollapse('monetary')">
            <span class="bk-ws-sec__chk" @click.stop><b-form-checkbox :checked="allOn('monetary', ids(selectableMonetary))"
                             :indeterminate="someOn('monetary', ids(selectableMonetary))"
                             :disabled="!selectableMonetary.length"
                             @change="v => setMonetary(selectableMonetary, v)"/></span>
            <i class="fa-solid fa-coins bk-ws-sec__icon"></i>
            <div class="bk-ws-sec__title">{{ $t('backup.workspace.monetary') }}</div>
            <section-counts :restorable="selectableMonetary.length" :total="monetary.length"/>
            <div class="bk-ws-sec__hint">{{ $t('backup.workspace.monetaryHint') }}</div>
            <i class="fa-solid bk-ws-sec__chev" :class="collapsed.monetary ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
          </div>
          <div v-show="!collapsed.monetary" class="bk-ws-sec__body">
            <table class="bk-ws-table">
              <thead><tr>
                <th></th><th>{{ $t('backup.columns.time') }}</th><th>{{ $t('backup.columns.type') }}</th>
                <th class="text-right">{{ $t('backup.columns.amount') }}</th><th>{{ $t('backup.columns.monetaryStatus') }}</th>
                <th>{{ $t('backup.columns.realType') }}</th><th>{{ $t('backup.columns.account') }}</th>
                <th>{{ $t('backup.columns.match') }}</th><th>{{ $t('backup.applyToBalance') }}</th>
              </tr></thead>
              <tbody>
              <tr v-for="m in visible(monetary)" :key="m.backupId" :class="rowCls(isRestorableRow(m), sel.monetary[m.backupId])"
                  @click="isRestorableRow(m) && setMonetary([m], !sel.monetary[m.backupId])">
                <td class="bk-ws-table__chk" @click.stop>
                  <b-form-checkbox :checked="!!sel.monetary[m.backupId]" :disabled="!isRestorableRow(m)"
                                   @change="v => setMonetary([m], v)"/>
                </td>
                <td><div class="bk-two"><span>{{ bkDateTime(m.registrationTime) }}</span><small class="bk-id">#{{ m.backupId }}</small></div></td>
                <td><div class="bk-two"><span class="font-weight-bold">{{ bkTypeLabel(m.type) }}</span><small>{{ bkActionLabel(m.action) }}</small></div></td>
                <td class="text-right"><span class="bk-amt" :class="bkAmountClass(m)">{{ bkMoney(m.amount, m.currency) }}</span></td>
                <td><span class="bk-mstatus" :class="`bk-mstatus--${(m.status || '').toLowerCase()}`">{{ m.status }}</span></td>
                <td>
                  <span class="bk-tag" :class="{ 'bk-tag--fake': m.realType === 'FAKE' }">{{ m.realType }}</span>
                  <span v-if="m.isFtd" class="bk-tag bk-tag--ftd">{{ m.ftdType || 'FTD' }}</span>
                  <span v-if="m.isStd" class="bk-tag bk-tag--std">STD</span>
                </td>
                <td>{{ m.accountId || '—' }}
                  <i v-if="m.currentAccountExists === false" class="fa-solid fa-circle-exclamation text-warning ml-25"
                     v-b-tooltip.hover.top="$t('backup.accountMissingNow')"></i>
                </td>
                <td><backup-status-badge :status="m.matchStatus" :reason="m.matchReason" :current-id="m.currentId" :client-id="m.currentClientId"/></td>
                <td @click.stop>
                  <backup-balance-switch v-if="isRestorableRow(m)" :value="!!sel.balance[m.backupId]"
                                         :allowed="canBalance(m)" :blocked-reason="balanceBlockReason(m)"
                                         @input="v => setBalance(m, v)"/>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- Trades -->
        <section v-if="trades.length" class="bk-ws-sec">
          <div class="bk-ws-sec__head" @click="toggleCollapse('trades')">
            <span class="bk-ws-sec__chk" @click.stop><b-form-checkbox :checked="allOn('trades', ids(selectableTrades))"
                             :indeterminate="someOn('trades', ids(selectableTrades))"
                             :disabled="!selectableTrades.length"
                             @change="v => setTrades(selectableTrades, v)"/></span>
            <i class="fa-solid fa-chart-line bk-ws-sec__icon"></i>
            <div class="bk-ws-sec__title">{{ $t('backup.workspace.trades') }}</div>
            <section-counts :restorable="selectableTrades.length" :total="trades.length"/>
            <div class="bk-ws-sec__hint">{{ $t('backup.workspace.tradesHint') }}</div>
            <i class="fa-solid bk-ws-sec__chev" :class="collapsed.trades ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
          </div>
          <div v-show="!collapsed.trades" class="bk-ws-sec__body">
            <table class="bk-ws-table">
              <thead><tr>
                <th></th><th>{{ $t('backup.columns.symbol') }}</th><th>{{ $t('backup.columns.volume') }}</th>
                <th>{{ $t('backup.columns.open') }}</th><th>{{ $t('backup.columns.close') }}</th>
                <th class="text-right">{{ $t('backup.columns.profit') }}</th><th>{{ $t('backup.columns.account') }}</th>
                <th>{{ $t('backup.columns.match') }}</th>
              </tr></thead>
              <tbody>
              <tr v-for="t in visible(trades)" :key="t.backupId" :class="rowCls(isTradeSelectable(t), sel.trades[t.backupId])"
                  @click="isTradeSelectable(t) && setTrades([t], !sel.trades[t.backupId])">
                <td class="bk-ws-table__chk" @click.stop>
                  <span v-b-tooltip.hover.right="tradeBlockReason(t)">
                    <b-form-checkbox :checked="!!sel.trades[t.backupId]" :disabled="!isTradeSelectable(t)"
                                     @change="v => setTrades([t], v)"/>
                  </span>
                </td>
                <td>
                  <span class="font-weight-bold">{{ t.symbol }}</span>
                  <span class="bk-side" :class="String(t.side || '').toUpperCase().indexOf('BUY') === 0 ? 'bk-side--buy' : 'bk-side--sell'">{{ t.side }}</span>
                  <span v-if="t.state === 'OPEN'" class="bk-tag bk-tag--open" v-b-tooltip.hover.top="$t('backup.openTradeHint')">
                    <i class="fa-solid fa-lock mr-25"></i>{{ $t('backup.viewOnly') }}
                  </span>
                </td>
                <td>{{ t.volume }}</td>
                <td><div class="bk-two"><span>{{ bkDateTime(t.openTime) }}</span><small>@ {{ t.openPrice }}</small></div></td>
                <td><div v-if="t.closeTime" class="bk-two"><span>{{ bkDateTime(t.closeTime) }}</span><small>@ {{ t.closePrice }}</small></div><span v-else class="text-muted">—</span></td>
                <td class="text-right"><span class="bk-amt" :class="Number(t.profit) >= 0 ? 'bk-amt--in' : 'bk-amt--out'">{{ fmtNum(t.profit) }}</span></td>
                <td>{{ t.accountId }}</td>
                <td><backup-status-badge :status="t.matchStatus" :reason="t.matchReason" :current-id="t.currentId"/></td>
              </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- Notes -->
        <section v-if="notes.length" class="bk-ws-sec">
          <div class="bk-ws-sec__head" @click="toggleCollapse('notes')">
            <span class="bk-ws-sec__chk" @click.stop><b-form-checkbox :checked="allOn('notes', ids(selectableNotes))"
                             :indeterminate="someOn('notes', ids(selectableNotes))"
                             :disabled="!selectableNotes.length"
                             @change="v => setNotes(selectableNotes, v)"/></span>
            <i class="fa-regular fa-note-sticky bk-ws-sec__icon"></i>
            <div class="bk-ws-sec__title">{{ $t('backup.workspace.notes') }}</div>
            <section-counts :restorable="selectableNotes.length" :total="notes.length"/>
            <i class="fa-solid bk-ws-sec__chev" :class="collapsed.notes ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
          </div>
          <div v-show="!collapsed.notes" class="bk-ws-sec__body">
            <div v-for="n in visible(notes)" :key="n.backupId" class="bk-ws-note-row" :class="rowCls(isRestorableRow(n), sel.notes[n.backupId])"
                 @click="isRestorableRow(n) && setNotes([n], !sel.notes[n.backupId])">
              <span @click.stop><b-form-checkbox :checked="!!sel.notes[n.backupId]" :disabled="!isRestorableRow(n)" @change="v => setNotes([n], v)"/></span>
              <div class="bk-ws-note-row__main">
                <div class="bk-ws-note-row__meta">
                  <i v-if="n.isPinned" class="fa-solid fa-thumbtack text-primary"></i>
                  <i v-if="n.isImportant" class="fa-solid fa-star text-warning"></i>
                  <span class="font-weight-bold">{{ bkUserName(n.userId) || '—' }}</span>
                  <span>{{ bkDateTime(n.time) }}</span>
                  <span class="bk-id">#{{ n.backupId }}</span>
                  <backup-status-badge class="ml-auto" :status="n.matchStatus" :reason="n.matchReason" :current-id="n.currentId"/>
                </div>
                <div class="bk-ws-note-row__text">{{ n.text }}</div>
              </div>
            </div>
          </div>
        </section>

        <div v-if="!diffs.length && !accounts.length && !monetary.length && !trades.length && !notes.length && !clientRestorable" class="bk-ws-center">
          <i class="fa-regular fa-circle-check bk-ws-center__icon text-success"></i>
          <div class="bk-ws-center__title">{{ $t('backup.workspace.nothingHere') }}</div>
        </div>
      </div>

      <!-- ============ STICKY FOOTER ============ -->
      <div class="bk-ws-footer">
        <div class="bk-ws-footer__summary">
          <div v-if="selectedTotal" class="bk-ws-footer__sentence">{{ summarySentence }}</div>
          <div v-else class="bk-ws-footer__empty">{{ $t('backup.workspace.nothingSelected') }}</div>
          <div v-if="balanceSelected.length" class="bk-ws-footer__balance">
            <i class="fa-solid fa-scale-unbalanced"></i>
            {{ $tc('backup.bulk.toBalance', balanceSelected.length, { n: balanceSelected.length }) }}
            <span v-for="t in totals" :key="t.currency" class="bk-bulk-amt">{{ bkMoney(t.total, t.currency) }}</span>
          </div>
        </div>
        <b-button variant="link" class="bk-ghost" @click="show = false">{{ $t('backup.close') }}</b-button>
        <b-button variant="primary" class="bk-ws-footer__go" :disabled="!selectedTotal" @click="emitPlan">
          <i class="fa-solid fa-rotate-left mr-50"></i>{{ $t('backup.restoreSelected') }}
          <span v-if="selectedTotal" class="bk-ws-footer__n">{{ selectedTotal }}</span>
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex'
import mixinNotifications from '@/components/mixin/mixinNotifications'
import mixinBackup, { isRestorable, emptyRestoreRequest, balanceTotals } from '@/components/mixin/mixinBackup'
import BackupStatusBadge from '@/components/partial/backup/BackupStatusBadge.vue'
import BackupBalanceSwitch from '@/components/partial/backup/BackupBalanceSwitch.vue'

const SectionCounts = {
  name: 'SectionCounts',
  functional: true,
  props: { restorable: Number, total: Number },
  render(h, { props, parent }) {
    return h('span', { class: 'bk-ws-counts' }, [
      h('span', { class: 'bk-ws-count bk-ws-count--todo' }, parent.$tc('backup.workspace.nRestorable', props.restorable, { n: props.restorable })),
      h('span', { class: 'bk-ws-count' }, parent.$t('backup.workspace.nTotal', { n: props.total })),
    ])
  },
}

const emptySel = () => ({ client: false, fields: {}, accounts: {}, monetary: {}, balance: {}, trades: {}, notes: {} })
const ORDER = { MISSING: 0, ID_CONFLICT: 1, CHANGED: 2, EXISTS: 3, RESTORED: 4 }
const byStatus = list => [...(list || [])].sort((a, b) => (ORDER[a.matchStatus] ?? 9) - (ORDER[b.matchStatus] ?? 9))

export default {
  name: 'BackupClientWorkspace',
  components: { BackupStatusBadge, BackupBalanceSwitch, SectionCounts },
  mixins: [mixinNotifications, mixinBackup],
  data: () => ({
    show: false,
    backupId: null,
    loading: false,
    error: null,
    detail: null,
    sel: emptySel(),
    hideExisting: false,
    collapsed: {},
    loadSeq: 0,
  }),
  computed: {
    client() {
      return this.detail && this.detail.client
    },
    clientRestorable() {
      return !!this.client && isRestorable(this.client.matchStatus)
    },
    diffs() {
      return (this.detail && this.detail.diffs) || []
    },
    accounts() {
      return byStatus(this.detail && this.detail.accounts)
    },
    monetary() {
      return byStatus(this.detail && this.detail.monetary)
    },
    trades() {
      return byStatus(this.detail && this.detail.trades)
    },
    notes() {
      return byStatus(this.detail && this.detail.notes)
    },
    accountById() {
      return this.accounts.reduce((m, a) => { m[a.backupId] = a; return m }, {})
    },
    selectableAccounts() {
      return this.accounts.filter(a => this.isAccountSelectable(a))
    },
    selectableMonetary() {
      return this.monetary.filter(m => this.isRestorableRow(m))
    },
    selectableTrades() {
      return this.trades.filter(t => this.isTradeSelectable(t))
    },
    selectableNotes() {
      return this.notes.filter(n => this.isRestorableRow(n))
    },
    anythingRestorable() {
      return this.clientRestorable || this.selectableAccounts.length || this.selectableMonetary.length
        || this.selectableTrades.length || this.selectableNotes.length
    },
    balanceSelected() {
      return this.monetary.filter(m => this.sel.monetary[m.backupId] && this.sel.balance[m.backupId])
    },
    totals() {
      return balanceTotals(this.balanceSelected.map(row => ({ row, applyToBalance: true })))
    },
    selectedTotal() {
      return (this.sel.client && this.clientRestorable ? 1 : 0)
        + ['fields', 'accounts', 'monetary', 'trades', 'notes'].reduce((t, k) => t + this.selectedIds(k).length, 0)
    },
    summarySentence() {
      const parts = []
      if (this.sel.client && this.clientRestorable) parts.push(this.$t('backup.summary.client'))
      const n = k => this.selectedIds(k).length
      if (n('accounts')) parts.push(this.$tc('backup.summary.accounts', n('accounts'), { n: n('accounts') }))
      if (n('fields')) parts.push(this.$tc('backup.summary.fields', n('fields'), { n: n('fields') }))
      if (n('monetary')) {
        let s = this.$tc('backup.summary.monetary', n('monetary'), { n: n('monetary') })
        if (this.balanceSelected.length) s += ` (${this.$tc('backup.summary.toBalance', this.balanceSelected.length, { n: this.balanceSelected.length })})`
        parts.push(s)
      }
      if (n('trades')) parts.push(this.$tc('backup.summary.trades', n('trades'), { n: n('trades') }))
      if (n('notes')) parts.push(this.$tc('backup.summary.notes', n('notes'), { n: n('notes') }))
      const text = parts.join(', ')
      return text.charAt(0).toUpperCase() + text.slice(1)
    },
    calloutIcon() {
      return {
        MISSING: 'fa-solid fa-circle-plus',
        ID_CONFLICT: 'fa-solid fa-triangle-exclamation',
        CHANGED: 'fa-solid fa-code-compare',
        EXISTS: 'fa-solid fa-circle-check',
        RESTORED: 'fa-solid fa-rotate-left',
      }[this.client && this.client.matchStatus] || 'fa-solid fa-circle-info'
    },
    calloutText() {
      const c = this.client || {}
      return this.$t(`backup.workspace.callout.${c.matchStatus || 'MISSING'}`, {
        id: c.backupId, current: c.currentId, reason: this.bkReasonLabel(c.matchReason),
      })
    },
  },
  methods: {
    ...mapActions('backup', ['getBackupClient']),
    open(backupId) {
      this.backupId = backupId
      this.detail = null
      this.sel = emptySel()
      this.collapsed = {}
      this.show = true
      this.load()
    },
    // Re-fetch (after a restore): statuses change, selection is cleared.
    reload() {
      if (!this.show || this.backupId == null) return
      this.sel = emptySel()
      this.load()
    },
    async load() {
      const seq = ++this.loadSeq
      this.loading = true
      this.error = null
      try {
        const detail = await this.getBackupClient(this.backupId)
        if (seq !== this.loadSeq) return
        this.detail = detail || null
      } catch (err) {
        if (seq !== this.loadSeq) return
        this.error = this.getErrorDescription(err)
      } finally {
        if (seq === this.loadSeq) this.loading = false
      }
    },
    onHidden() {
      this.loadSeq++
      this.detail = null
      this.sel = emptySel()
    },
    // ---------- generic selection helpers ----------
    ids(list) {
      return list.map(x => x.backupId)
    },
    selectedIds(key) {
      return Object.keys(this.sel[key] || {}).filter(k => this.sel[key][k])
    },
    allOn(key, ids) {
      return ids.length > 0 && ids.every(id => this.sel[key][id])
    },
    someOn(key, ids) {
      return !this.allOn(key, ids) && ids.some(id => this.sel[key][id])
    },
    patch(key, changes) {
      const next = { ...this.sel[key] }
      Object.keys(changes).forEach(id => {
        if (changes[id]) next[id] = true
        else delete next[id]
      })
      this.sel = { ...this.sel, [key]: next }
    },
    setOne(key, id, on) {
      this.patch(key, { [id]: on })
    },
    setMany(key, ids, on) {
      this.patch(key, ids.reduce((m, id) => { m[id] = on; return m }, {}))
    },
    visible(list) {
      return this.hideExisting ? list.filter(r => isRestorable(r.matchStatus)) : list
    },
    rowCls(selectable, on) {
      return { 'is-selectable': selectable, 'is-on': !!on, 'is-locked': !selectable }
    },
    toggleCollapse(key) {
      this.collapsed = { ...this.collapsed, [key]: !this.collapsed[key] }
    },
    // ---------- rules ----------
    isRestorableRow(row) {
      return isRestorable(row.matchStatus)
    },
    isAccountSelectable(a) {
      return isRestorable(a.matchStatus)
    },
    // Account usable by a restored trade/balance: exists now or ticked for restore.
    accountWillExist(accountId) {
      if (accountId == null) return false
      if (this.sel.accounts[accountId]) return true
      const a = this.accountById[accountId]
      return !!a && !isRestorable(a.matchStatus)
    },
    accountRestorable(accountId) {
      const a = this.accountById[accountId]
      return !!a && isRestorable(a.matchStatus)
    },
    isTradeSelectable(t) {
      if (t.state !== 'CLOSED' || !isRestorable(t.matchStatus)) return false
      return t.restorable === true || this.accountRestorable(t.accountId)
    },
    tradeBlockReason(t) {
      if (this.isTradeSelectable(t)) {
        return t.restorable === true || this.accountWillExist(t.accountId) ? '' : this.$t('backup.workspace.tradeNeedsAccount', { account: t.accountId })
      }
      if (t.state === 'OPEN') return this.$t('backup.openTradeHint')
      if (!isRestorable(t.matchStatus)) return this.$t('backup.notRestorable')
      return this.$t('backup.tradeAccountMissing')
    },
    canBalance(m) {
      if (m.status !== 'APPROVED' || m.action === 'TAXES') return false
      if (m.currentAccountExists === false) return this.accountWillExist(m.accountId)
      return m.canApplyBalance === true
    },
    balanceBlockReason(m) {
      if (this.canBalance(m)) return ''
      if (m.status !== 'APPROVED') return this.$t('backup.balanceBlock.notApproved')
      if (m.action === 'TAXES') return this.$t('backup.balanceBlock.taxes')
      if (m.currentAccountExists === false) {
        return this.accountRestorable(m.accountId)
          ? this.$t('backup.balanceBlock.selectAccount', { account: m.accountId })
          : this.$t('backup.balanceBlock.noAccount', { account: m.accountId || '' })
      }
      return this.$t('backup.balanceBlock.generic')
    },
    // ---------- setters with dependency handling ----------
    ensureClient() {
      if (this.clientRestorable && !this.sel.client) this.sel = { ...this.sel, client: true }
    },
    setClient(on) {
      if (on) {
        this.sel = { ...this.sel, client: true }
        return
      }
      // Without the client nothing that belongs to it can be restored.
      this.sel = { ...emptySel(), fields: this.sel.fields }
    },
    setAccounts(list, on) {
      const ids = this.ids(list.filter(a => this.isAccountSelectable(a)))
      if (!ids.length) return
      if (on) this.ensureClient()
      this.setMany('accounts', ids, on)
      if (!on) {
        // Drop what depended on these accounts.
        const balanceOff = this.monetary.filter(m => ids.includes(m.accountId) && m.currentAccountExists === false)
        this.setMany('balance', this.ids(balanceOff), false)
        const tradesOff = this.trades.filter(t => ids.includes(t.accountId) && t.restorable !== true)
        this.setMany('trades', this.ids(tradesOff), false)
      }
    },
    setMonetary(list, on) {
      const ids = this.ids(list.filter(m => this.isRestorableRow(m)))
      if (!ids.length) return
      if (on) this.ensureClient()
      this.setMany('monetary', ids, on)
      if (!on) this.setMany('balance', ids, false)
    },
    setBalance(m, on) {
      if (on && !this.canBalance(m)) return
      if (on && !this.sel.monetary[m.backupId]) this.setMonetary([m], true)
      this.setOne('balance', m.backupId, on)
    },
    setTrades(list, on) {
      const ok = list.filter(t => this.isTradeSelectable(t))
      if (!ok.length) return
      if (on) {
        this.ensureClient()
        // A trade whose account is missing now brings its account along.
        const needAccounts = ok.filter(t => t.restorable !== true && !this.accountWillExist(t.accountId))
          .map(t => this.accountById[t.accountId]).filter(Boolean)
        if (needAccounts.length) this.setAccounts(needAccounts, true)
      }
      this.setMany('trades', this.ids(ok), on)
    },
    setNotes(list, on) {
      const ids = this.ids(list.filter(n => this.isRestorableRow(n)))
      if (!ids.length) return
      if (on) this.ensureClient()
      this.setMany('notes', ids, on)
    },
    selectAllMissing() {
      if (this.clientRestorable) this.sel = { ...this.sel, client: true }
      this.setAccounts(this.selectableAccounts, true)
      this.setMonetary(this.selectableMonetary, true) // record only — never balance
      this.setTrades(this.selectableTrades, true)
      this.setNotes(this.selectableNotes, true)
    },
    clearSelection() {
      this.sel = emptySel()
    },
    fmtNum(v) {
      const n = Number(v) || 0
      return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    // ---------- hand the plan to the confirmation modal ----------
    emitPlan() {
      if (!this.selectedTotal || !this.client) return
      const req = emptyRestoreRequest(this.client.backupId)
      req.restoreClient = this.clientRestorable && this.sel.client === true
      req.accounts = this.selectedIds('accounts')
      req.fields = this.selectedIds('fields')
      const monetary = this.monetary.filter(m => this.sel.monetary[m.backupId])
        .map(row => ({ row, applyToBalance: !!this.sel.balance[row.backupId] && this.canBalance(row) }))
      req.monetary = monetary.map(m => ({ backupId: m.row.backupId, applyToBalance: m.applyToBalance }))
      req.trades = this.selectedIds('trades')
      req.notes = this.selectedIds('notes')
      this.$emit('restore-plan', {
        source: 'workspace',
        clientName: this.bkClientName(this.client),
        sections: {
          client: req.restoreClient ? this.client : null,
          accounts: this.accounts.filter(a => this.sel.accounts[a.backupId]),
          fields: this.diffs.filter(d => this.sel.fields[d.field]),
          monetary,
          trades: this.trades.filter(t => this.sel.trades[t.backupId]),
          notes: this.notes.filter(n => this.sel.notes[n.backupId]),
        },
        requests: [req],
      })
    },
  },
}
</script>
